import React, { useEffect, useState } from "react";
import { Box, Card, Collapse, Typography, Tabs, Tab } from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import PreviewActionModal from "@components/Modal/PreviewActionModal";
import ReceiptIcon from "@mui/icons-material/Receipt";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import dayjs from "dayjs";
import { useDispatch } from "react-redux";
import { openFileBySignedUrl } from "../../../utils/getViewSignedUrlHandler";
import {
  getFileSignedURLMorthThunk,
  getFileSignedURLThunk,
} from "@modules/stepper-row/redux/thunk/documentThunk";
import { formatReceiptDate } from "../../../utils/formatNocDate";
import { getActivityLogsThunk } from "@modules/application-section/redux/thunk/activityThunk";
import { getObjectKey } from "@components/buttonGroupView/utils";

interface ApplicationStatusProps {
  activityLogs: any[];
  receiptDetails: any[];
  detailsData?: any;
}

export const ApplicationStatus: React.FC<ApplicationStatusProps> = React.memo(
  ({ activityLogs, receiptDetails, detailsData }) => {
    const [open, setOpen] = useState(true);
    const [tabValue, setTabValue] = useState(0);
    const [isPreviewActionModalOpen, setIsPreviewActionModalOpen] =
      useState(false);

    const [selectedReceipt, setSelectedReceipt] = useState<any>(null);
    const [activityLogsData, setActivityLogsData] = useState<any[]>([]);
    const [appealOpen, setAppealOpen] = useState<string | null>(null);
    const dispatch = useDispatch();

    useEffect(() => {
      if (detailsData?.application_id) {
        console.log(
          "Fetching activity logs for application_id:",
          detailsData.application_id,
        );
        (
          dispatch(
            getActivityLogsThunk(detailsData.application_id) as any,
          ) as any
        )
          .unwrap()
          .then((res: any) => {
            console.log("Activity logs API response:", res);
            const logsData = Array.isArray(res?.data)
              ? res.data
              : res?.logs || [];
            setActivityLogsData(logsData);
          })
          .catch((error: any) => {
            console.error("Error fetching activity logs:", error);
          });
      } else {
        console.log("No access_id provided");
      }
    }, [detailsData?.id, dispatch]);

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
      setTabValue(newValue);
    };

    const handleReceiptClick = (receipt: any) => {
      setSelectedReceipt(receipt);
      setIsPreviewActionModalOpen(true);
    };

    const formatLicenseDate = (dateString: string) => {
      if (!dateString) return "—";

      // For values like 2026-06-10T00:00:00.000Z
      if (dateString.endsWith("T00:00:00.000Z")) {
        const date = new Date(dateString);

        const month = String(date.getUTCMonth() + 1).padStart(2, "0");
        const day = String(date.getUTCDate()).padStart(2, "0");
        const year = date.getUTCFullYear();

        return `${month}/${day}/${year}`; // MM/DD/YYYY
      }

      return formatReceiptDate(dateString);
    };

    // const handleDownload = (filePath: string | null | undefined) => {
    //   if (!filePath) return;
    //   const url =
    //     process.env.REACT_APP_API_IMAGE_URL +
    //     "morth-documents/access/" +
    //     filePath;
    //   window.open(url, "_blank");
    // };

    const handleViewFile = (doc_name: any) => {
      openFileBySignedUrl({
        document_name: getObjectKey(doc_name),
        dispatch,
        getSignedUrlThunk: doc_name.includes("noc")
          ? getFileSignedURLThunk
          : getFileSignedURLMorthThunk,
      });
    };

    const handleViewAppealDocument = (document: any) => {
      if (!document?.upload_path) return;

      openFileBySignedUrl({
        document_name: getObjectKey(document.upload_path),
        dispatch,
        getSignedUrlThunk: document?.upload_path?.includes("noc")
          ? getFileSignedURLThunk
          : getFileSignedURLMorthThunk,
      });
    };

    function formatDateTime(dateString: string) {
      if (!dateString) return "";
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      const yyyy = date.getFullYear();
      const mm = String(date.getMonth() + 1).padStart(2, "0");
      const dd = String(date.getDate()).padStart(2, "0");
      const hh = String(date.getHours()).padStart(2, "0");
      const min = String(date.getMinutes()).padStart(2, "0");
      const ss = String(date.getSeconds()).padStart(2, "0");
      // return `${yyyy}/${mm}/${dd} ${hh}:${min}:${ss}`;
      return `${dd}/${mm}/${yyyy} ${hh}:${min}:${ss}`;
    }

    const paymentStageMap = {
      1: "Application Processing Fees Receipt",
      2: "Provisional NOC Fees Receipt",
      3: "Final NOC Fees Receipt",
      4: "Renewal Fees Receipt",
    };

    const hasAppeal =
      Array.isArray(detailsData?.appealRequestData) &&
      detailsData.appealRequestData.some(
        (appeal: any) => !!appeal?.appeal_remark?.trim(),
      );

    useEffect(() => {
      if (!hasAppeal && tabValue === 2) {
        setTabValue(0);
      }
    }, [hasAppeal, tabValue]);

    return (
      <>
        <PreviewActionModal
          open={isPreviewActionModalOpen}
          onClose={() => setIsPreviewActionModalOpen(false)}
          receiptDetails={selectedReceipt ? [selectedReceipt] : []}
        />

        <Card
          sx={{ mt: 2, boxShadow: "none", border: "1px solid #E7E8E9", pb: 2 }}
        >
          <Box
            sx={{
              backgroundColor: "#F4F2F7",
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
            }}
          >
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              sx={{
                "& .MuiTab-root": {
                  textTransform: "none",
                  fontFamily: "Inter, sans-serif",
                  fontWeight: 500,
                  fontSize: "16px",
                  color: "#666",
                },
                "& .Mui-selected": {
                  color: "#212121 !important",
                  fontWeight: 500,
                },
                "& .MuiTabs-indicator": {
                  backgroundColor: "#212121",
                },
              }}
            >
              <Tab label="Application Stages" />
              <Tab label="Activity Logs" />
              {hasAppeal && <Tab label="Appeal" />}
            </Tabs>
          </Box>

          {/* Application Stages Tab */}
          {tabValue === 0 && (
            <>
              {activityLogs.map((log, index) => {
                return (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 2,
                      pl: 2,
                      pr: 2,
                      pt: index === 0 ? 3 : 0,
                      pb: 0,
                      minHeight: 40,
                      //
                    }}
                  >
                    {/* Timeline: Circle + Line */}
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        width: 20,
                        minWidth: 20,
                      }}
                    >
                      <Box
                        sx={{
                          width: 20,
                          height: 20,
                          minWidth: 20,
                          backgroundColor:
                            log.type == "pending" ? "#E0E0E0" : "#3A1C73",
                          minHeight: 20,
                          aspectRatio: "1 / 1",
                          borderRadius: "50%",
                          border: `1px solid ${
                            log.type == "pending" ? "#E0E0E0" : "#3A1C73"
                          }`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: log.type == "pending" ? "#A0A0A0" : "#FFF",
                          fontWeight: "bold",
                          fontSize: 12,
                          boxSizing: "border-box",
                          cursor:
                            log.type == "pending" ? "not-allowed" : "default",
                          opacity: log.type == "pending" ? 0.6 : 1,
                        }}
                      >
                        {index + 1}
                      </Box>
                      {/* Vertical Line */}
                      {index < activityLogs.length - 1 && (
                        <Box
                          sx={{
                            width: 2,
                            height: 35,
                            backgroundColor:
                              log.type == "pending" ? "#A0A0A0" : "#3A1C73",
                            mt: 0.5,
                            mb: 0.5,
                            marginLeft: "auto",
                            marginRight: "auto",
                          }}
                        />
                      )}
                    </Box>
                    {/* Step Details */}
                    <Box>
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        color="text.primary"
                        fontSize={12}
                      >
                        {log.stage}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {log.timestamp}
                      </Typography>
                      {log.comments && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          fontSize={10}
                        >
                          {log.comments}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </>
          )}

          {/* Tab 1: Activity Logs */}
          {tabValue === 1 && (
            <>
              {activityLogsData.length > 0 ? (
                activityLogsData.map((log, index) => {
                  return (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 2,
                        pl: 2,
                        pr: 2,
                        pt: index === 0 ? 3 : 0,
                        pb: 1,
                        minHeight: 40,
                        //
                      }}
                    >
                      {/* Timeline: Circle + Line */}
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          width: 20,
                          minWidth: 20,
                        }}
                      >
                        <Box
                          sx={{
                            width: 20,
                            height: 20,
                            minWidth: 20,
                            backgroundColor:
                              log.type == "pending" ? "#E0E0E0" : "#3A1C73",
                            minHeight: 20,
                            aspectRatio: "1 / 1",
                            borderRadius: "50%",
                            border: `1px solid ${
                              log.type == "pending" ? "#E0E0E0" : "#3A1C73"
                            }`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: log.type == "pending" ? "#A0A0A0" : "#FFF",
                            fontWeight: "bold",
                            fontSize: 12,
                            boxSizing: "border-box",
                            cursor:
                              log.type == "pending" ? "not-allowed" : "default",
                            opacity: log.type == "pending" ? 0.6 : 1,
                          }}
                        >
                          {index + 1}
                        </Box>
                        {/* Vertical Line */}
                        {index < activityLogsData.length - 1 && (
                          <Box
                            sx={{
                              width: 2,
                              height: 30,
                              backgroundColor:
                                log.type == "pending" ? "#A0A0A0" : "#3A1C73",
                              mt: 1.0,
                            }}
                          />
                        )}
                      </Box>
                      {/* Step Details */}

                      <Box>
                        <Typography
                          variant="body2"
                          fontWeight="bold"
                          color="text.primary"
                          fontSize={12}
                        >
                          {log.comments}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {dayjs(log.created_at).format("DD/MM/YYYY hh:mm A")}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    py: 4,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    No Records Found
                  </Typography>
                </Box>
              )}
            </>
          )}
          {tabValue === 2 && hasAppeal && (
            <Box
              sx={{
                px: 1.5,
                pt: 1,
                pb: 1.5,
              }}
            >
              <Box
                sx={{
                  border: "1px solid #E4E4E7",
                  borderRadius: "5px",
                  overflow: "hidden",
                  backgroundColor: "#FFFFFF",
                }}
              >
                {[...(detailsData?.appealRequestData || [])]
                  .filter((appeal: any) => appeal?.appeal_remark?.trim())
                  .sort((a: any, b: any) => {
                    const dateA = new Date(
                      a?.sa_action_date || a?.created_at || 0,
                    ).getTime();

                    const dateB = new Date(
                      b?.sa_action_date || b?.created_at || 0,
                    ).getTime();

                    return dateB - dateA;
                  })
                  .map((appeal: any, index: number) => {
                    const requestKey = `${appeal.id}-request`;
                    const actionKey = `${appeal.id}-action`;

                    const isRequestOpen = appealOpen === requestKey;

                    const isActionOpen = appealOpen === actionKey;

                    const actionType = appeal?.sa_action_type?.toUpperCase();

                    const isApproved = actionType === "APPROVE";

                    const isRejected = actionType === "REJECT";

                    const actionTitle = isApproved
                      ? "Appeal Approved"
                      : isRejected
                        ? "Appeal Rejected"
                        : null;

                    const actionColor = isApproved ? "#2EAC5D" : "#C62828";

                    const actionBg = isApproved ? "#F1FAF4" : "#FEF3F3";

                    return (
                      <React.Fragment key={appeal?.id || index}>
                        {/* =====================================
                            APPROVED / REJECTED
                           ===================================== */}

                        {actionTitle && (
                          <Box
                            sx={{
                              backgroundColor: actionBg,
                              borderBottom: "1px solid #EEEEEE",
                            }}
                          >
                            <Box
                              onClick={() =>
                                setAppealOpen(isActionOpen ? null : actionKey)
                              }
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                px: 2,
                                py: 1.5,
                                cursor: "pointer",
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1.25,
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 14,
                                    height: 14,
                                    borderRadius: "50%",
                                    backgroundColor: actionColor,
                                    flexShrink: 0,
                                  }}
                                />

                                <Box>
                                  <Typography
                                    sx={{
                                      fontSize: 14,
                                      fontWeight: 600,
                                      color: actionColor,
                                      lineHeight: 1.3,
                                    }}
                                  >
                                    {actionTitle}
                                  </Typography>

                                  <Typography
                                    sx={{
                                      mt: 0.25,
                                      fontSize: 13,
                                      color: "#9EA1AA",
                                    }}
                                  >
                                    {appeal?.sa_action_date
                                      ? dayjs(appeal.sa_action_date).format(
                                          "DD MMM YYYY",
                                        )
                                      : ""}
                                  </Typography>
                                </Box>
                              </Box>

                              {isActionOpen ? (
                                <KeyboardArrowUpIcon
                                  sx={{
                                    fontSize: 22,
                                    color: "#222",
                                  }}
                                />
                              ) : (
                                <KeyboardArrowDownIcon
                                  sx={{
                                    fontSize: 22,
                                    color: "#222",
                                  }}
                                />
                              )}
                            </Box>

                            {/* Action details */}
                            <Collapse in={isActionOpen}>
                              <Box
                                sx={{
                                  px: 2,
                                  pb: 1.5,
                                }}
                              >
                                {appeal?.sa_remarks && (
                                  <Box
                                    sx={{
                                      backgroundColor: "#F7F7F9",
                                      border: "1px solid #E1E1E5",
                                      borderRadius: "7px",
                                      p: 1.5,
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        fontSize: 13,
                                        color: "#70727D",
                                        lineHeight: 1.5,
                                      }}
                                    >
                                      "{appeal.sa_remarks}"
                                    </Typography>
                                  </Box>
                                )}
                                <Typography
                                  sx={{
                                    color: isApproved ? "#154321" : "#B3261D",
                                    mt: 1,
                                    fontWeight: 600,
                                  }}
                                >
                                  {isApproved ? "Approved" : "Rejected"} by
                                  Super Admin
                                </Typography>
                              </Box>
                            </Collapse>
                          </Box>
                        )}

                        {/* =====================================
                            APPEAL REQUESTED
                           ===================================== */}

                        <Box
                          sx={{
                            backgroundColor: "#FFFFFF",
                            borderBottom:
                              index < detailsData.appealRequestData.length - 1
                                ? "1px solid #EEEEEE"
                                : "none",
                          }}
                        >
                          <Box
                            onClick={() =>
                              setAppealOpen(isRequestOpen ? null : requestKey)
                            }
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              px: 2,
                              py: 1.5,
                              cursor: "pointer",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1.25,
                              }}
                            >
                              <Box
                                sx={{
                                  width: 14,
                                  height: 14,
                                  borderRadius: "50%",
                                  border: "2px solid #3A1C73",
                                  backgroundColor: "#FFFFFF",
                                  flexShrink: 0,
                                }}
                              />

                              <Box>
                                <Typography
                                  sx={{
                                    fontSize: 14,
                                    fontWeight: 600,
                                    color: "#292929",
                                    lineHeight: 1.3,
                                  }}
                                >
                                  Appeal Requested
                                </Typography>

                                <Typography
                                  sx={{
                                    mt: 0.25,
                                    fontSize: 13,
                                    color: "#9EA1AA",
                                  }}
                                >
                                  {appeal?.created_at
                                    ? dayjs(appeal.created_at).format(
                                        "DD MMM YYYY",
                                      )
                                    : ""}
                                </Typography>
                              </Box>
                            </Box>

                            {isRequestOpen ? (
                              <KeyboardArrowUpIcon
                                sx={{
                                  fontSize: 22,
                                  color: "#222",
                                }}
                              />
                            ) : (
                              <KeyboardArrowDownIcon
                                sx={{
                                  fontSize: 22,
                                  color: "#222",
                                }}
                              />
                            )}
                          </Box>

                          {/* Request details */}
                          <Collapse in={isRequestOpen}>
                            <Box
                              sx={{
                                px: 2,
                                pb: 1.5,
                              }}
                            >
                              <Box
                                sx={{
                                  backgroundColor: "#F7F7F9",
                                  border: "1px solid #E1E1E5",
                                  borderRadius: "7px",
                                  p: 1.5,
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontSize: 13,
                                    color: "#70727D",
                                    lineHeight: 1.5,
                                  }}
                                >
                                  "{appeal.appeal_remark}"
                                </Typography>

                                {appeal?.document && (
                                  <>
                                    <Box
                                      sx={{
                                        borderTop: "1px solid #DDDEE2",
                                        my: 1.25,
                                      }}
                                    />

                                    <Box
                                      onClick={() =>
                                        handleViewAppealDocument(
                                          appeal.document,
                                        )
                                      }
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 0.8,
                                        cursor: "pointer",
                                      }}
                                    >
                                      <ReceiptIcon
                                        sx={{
                                          fontSize: 17,
                                          color: "#3A1C73",
                                        }}
                                      />

                                      <Typography
                                        sx={{
                                          fontSize: 13,
                                          color: "#3A1C73",
                                          textDecoration: "underline",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap",
                                        }}
                                      >
                                        {appeal.document.document_name ||
                                          appeal.document.file_name}
                                      </Typography>
                                    </Box>
                                  </>
                                )}
                              </Box>
                            </Box>
                          </Collapse>
                        </Box>
                      </React.Fragment>
                    );
                  })}

                {/* =====================================
                    HISTORY FOOTER
                   ===================================== */}

                <Box
                  sx={{
                    backgroundColor: "#FAF9FC",
                    borderTop: "1px solid #EEEEEE",
                    px: 2,
                    py: 1.75,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 11,
                      color: "#A3A5AE",
                      lineHeight: 1.45,
                    }}
                  >
                    History records all official decisions made on this
                    application.
                    <br />
                    Tap any entry to expand the full remark and any attached
                    documents.
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </Card>
        {(detailsData?.provisional_licence_url ||
          detailsData?.provisional_license_url ||
          detailsData?.final_license_url ||
          detailsData?.final_licence_url ||
          detailsData?.public_demand_url ||
          (Array.isArray(receiptDetails) && receiptDetails.length > 0)) && (
          <Card
            sx={{
              mt: 2,
              boxShadow: "none",
              border: "1px solid #E7E8E9",
              pb: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 2,
                py: 1.5,
                borderBottom: "1px solid #E0E0E0",
                bgcolor: "#F9FAFB",
                cursor: "pointer",
              }}
              onClick={() => setOpen((prev) => !prev)}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <ReceiptIcon sx={{ color: "#212121" }} />
                <Typography
                  sx={{
                    ml: 1.5,
                    fontWeight: 500,
                    fontSize: 16,
                    color: "#212121",
                  }}
                >
                  Receipts and Documents
                </Typography>
              </Box>
              {open ? (
                <KeyboardArrowUpIcon sx={{ color: "#212121" }} />
              ) : (
                <KeyboardArrowDownIcon sx={{ color: "#212121" }} />
              )}
            </Box>

            <Box>
              <Collapse in={open}>
                <Box sx={{ px: 2, py: 1 }}>
                  {Array.isArray(receiptDetails) &&
                    receiptDetails.map((data: any) => (
                      <Box
                        key={data.receiptNumber}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          py: 0.5,
                        }}
                      >
                        {/* Label and transactionDate on the left */}
                        <Box>
                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontWeight: 700,
                              color: "#000",
                              fontSize: 14,
                              cursor: "pointer",
                              fontFamily: "Inter, sans-serif",
                            }}
                            onClick={() => handleReceiptClick(data)}
                          >
                            {paymentStageMap[
                              data?.paymentStageId as keyof typeof paymentStageMap
                            ] || data?.paymentStage}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "#8B8B8B",
                              fontSize: 12,
                              fontFamily: "Inter, sans-serif",
                              mt: 0.5,
                            }}
                          >
                            {(data?.transactionDate &&
                              formatReceiptDate(data?.transactionDate)) ||
                              ""}
                          </Typography>
                        </Box>
                        {/* Eye icon on the right */}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <VisibilityOutlinedIcon
                            sx={{
                              color: "#000000",
                              fontSize: 16,
                              cursor: "pointer",
                            }}
                            onClick={() => handleReceiptClick(data)}
                          />
                        </Box>
                      </Box>
                    ))}

                  {detailsData?.renewal_license_url && (
                    <Box
                      sx={{
                        mt: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        cursor: "pointer",
                        fontWeight: 400,
                        color: "#000",
                        fontSize: 12,
                        fontFamily: "Inter, sans-serif",
                      }}
                      onClick={() =>
                        handleViewFile(detailsData?.renewal_license_url)
                      }
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          flex: 1,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#000000",
                            fontWeight: 700,
                            fontSize: 14,
                          }}
                        >
                          Download Renewal NOC
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#8B8B8B",
                            fontSize: 12,
                            fontFamily: "Inter, sans-serif",
                            mt: 0.5,
                          }}
                        >
                          {/* {detailsData.final_license_date || ""} */}
                          {detailsData?.renewal_license_date &&
                            formatReceiptDate(
                              detailsData?.renewal_license_date,
                            )}
                        </Typography>
                      </Box>
                      <FileDownloadOutlinedIcon
                        sx={{ fontSize: 18, color: "#000000" }}
                      />
                    </Box>
                  )}

                  {detailsData?.final_licence_url && (
                    <Box
                      sx={{
                        mt: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        cursor: "pointer",
                        fontWeight: 400,
                        color: "#000",
                        fontSize: 12,
                        fontFamily: "Inter, sans-serif",
                      }}
                      onClick={() =>
                        handleViewFile(detailsData?.final_licence_url)
                      }
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          flex: 1,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#000000",
                            fontWeight: 700,
                            fontSize: 14,
                          }}
                        >
                          Download Final NOC
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#8B8B8B",
                            fontSize: 12,
                            fontFamily: "Inter, sans-serif",
                            mt: 0.5,
                          }}
                        >
                          {/* {detailsData.final_license_date || ""} */}
                          {detailsData?.final_license_date &&
                            formatLicenseDate(detailsData?.final_license_date)}
                        </Typography>
                      </Box>
                      <FileDownloadOutlinedIcon
                        sx={{ fontSize: 18, color: "#000000" }}
                      />
                    </Box>
                  )}

                  {detailsData?.provisional_licence_url && (
                    <Box
                      sx={{
                        mt: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        cursor: "pointer",
                        fontWeight: 400,
                        color: "#000",
                        fontSize: 12,
                        fontFamily: "Inter, sans-serif",
                      }}
                      onClick={() =>
                        handleViewFile(detailsData?.provisional_licence_url)
                      }
                    >
                      {/* <Typography
                      variant="body2"
                      sx={{
                        color: "#000000",
                        fontWeight: 400,
                        fontSize: 12
                        // textDecoration: "underline"
                      }}
                    >
                      Download Provisional Licence
                    </Typography> */}
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          flex: 1,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#000000",
                            fontWeight: 700,
                            fontSize: 14,
                          }}
                        >
                          Download Provisional NOC
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#8B8B8B",
                            fontSize: 12,
                            fontFamily: "Inter, sans-serif",
                            mt: 0.5,
                          }}
                        >
                          {/* {detailsData.provisional_license_date || ""} */}
                          {detailsData?.provisional_license_date &&
                            formatLicenseDate(
                              detailsData?.provisional_license_date,
                            )}
                        </Typography>
                      </Box>
                      <FileDownloadOutlinedIcon
                        sx={{ fontSize: 18, color: "#000000" }}
                      />
                    </Box>
                  )}

                  {detailsData?.public_demand_url &&
                    detailsData?.amenity?.includes("OFC") && (
                      <Box
                        sx={{
                          mt: 1,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          cursor: "pointer",
                          fontWeight: 400,
                          color: "#000",
                          fontSize: 12,
                          fontFamily: "Inter, sans-serif",
                        }}
                        onClick={() =>
                          handleViewFile(detailsData?.public_demand_url)
                        }
                      >
                        {/* <Typography
                      variant="body2"
                      sx={{
                        color: "#000000",
                        fontWeight: 400,
                        fontSize: 12
                        // textDecoration: "underline"
                      }}
                    >
                      Download Public Demand
                    </Typography> */}
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            flex: 1,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              color: "#000000",
                              fontWeight: 700,
                              fontSize: 14,
                            }}
                          >
                            Download Public Demand
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "#8B8B8B",
                              fontSize: 12,
                              fontFamily: "Inter, sans-serif",
                              mt: 0.5,
                            }}
                          >
                            {/* {detailsData.provisional_license_date || ""} */}
                            {detailsData?.public_demand_signed_at &&
                              formatReceiptDate(
                                detailsData?.public_demand_signed_at,
                              )}
                          </Typography>
                        </Box>
                        <FileDownloadOutlinedIcon
                          sx={{ fontSize: 18, color: "#000000" }}
                        />
                      </Box>
                    )}
                </Box>
              </Collapse>
            </Box>
          </Card>
        )}
      </>
    );
  },
);
